import React, { useEffect, useState } from 'react';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import MaterialDivider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import PhotoUploader from './PhotoUploader';
import { usePatientInformation } from '../../queries';
import {
    useStyles,
    ColorlibConnector,
    ColorlibStepIcon,
    ContainerPaper,
    GreyPaperContainer,
    BannerMessageContainer,
    PatientInfoContainer,
    VerifyManualContainer,
} from './Styling';
import {
    VerificationStatus, WorkflowType
} from './utils';
import {
    getPatientReferenceUrl,
    retryFacialRecognitionVerification,
    overrideFacialRecognitionTask,
    markFacialRecognitionTaskUnverified,
    markFacialRecognitionTaskVerified, finalisePatientReference
} from '../../Apis/FacialRecognition';

const ManualVerificationForm = ({ confirmed, setConfirmed, onSubmit }) => (
    <VerifyManualContainer style={{ paddingLeft: 0, paddingRight: 0, marginLeft: '0', marginRight: '0' }}>
        <h3>Verify patient identity manually</h3>
        <ol>
            <li>I confirm that I have personally verified the patient's identity using a valid form of identification.</li>
            <li>I understand that this action will be flagged and logged for audit purposes.</li>
        </ol>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <label>
                <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                />
                Confirm
            </label>
            <Button
                variant="outlined"
                color="primary"
                disabled={!confirmed}
                onClick={onSubmit}
                style={{ minWidth: '150px' }}
            >
                Submit
            </Button>
        </div>
    </VerifyManualContainer>
);

const ImageDisplay = ({ imageUrl, alt, className, children }) => {
    console.log(imageUrl)
    return (
        <div className={className}>
            {imageUrl ? (
                <img
                    src={imageUrl}
                    alt={alt}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
            ) : (
                <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    color: '#666',
                    fontSize: '14px'
                }}>
                </div>
            )}
            {children}
        </div>
    )
}

// workflowType and verification status comes in the differed task
const FacialIDVerification = ({
    task_id,
    patient,
    workflowType,
    verificationStatus,
    getFacialRecognitionPresignedUrl,
    restartVerification,
    imageUrl,
    onProceed
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const classes = useStyles();
    const [activeStep, setActiveStep] = useState(0);
    const [confirmed, setConfirmed] = useState(false);
    const [capturedImageUrl, setCapturedImageUrl] = useState(null);
    const [originalImageUrl, setOriginalImageUrl] = useState(null);
    const [showDeclaration, setShowDeclaration] = useState(false);
    const [declarationType, setDeclarationType] = useState('');
    const [declarationConfirmed, setDeclarationConfirmed] = useState(false);
    const [patientIdVerified, setPatientIdVerified] = useState(false);

    const patientInformationQuery = usePatientInformation(patient?.primary_identifier?.value);
    const demographics = patientInformationQuery.data ?? {};

    useEffect(() => {
        setCapturedImageUrl(imageUrl)
    }, [imageUrl]);

    useEffect(() => {
        if (patient?.primary_identifier?.value) {
            getPatientReferenceUrl(patient.primary_identifier.value).then(url => {
                setOriginalImageUrl(url);
            });
        }
    }, [patient]);

    useEffect(() => {
        if (activeStep === 1){

            const shouldAdvance = (
                (workflowType === WorkflowType.PATIENT_REFERENCE &&
                verificationStatus === VerificationStatus.FACE_DETECTED) ||

                ([WorkflowType.BOOKING_VERIFICATION, WorkflowType.PROCEDURE_VERIFICATION].includes(workflowType) &&
                (verificationStatus === VerificationStatus.FACE_MATCH) || (verificationStatus === VerificationStatus.NO_FACE_MATCH))
            );

            if (shouldAdvance) {
                setActiveStep(2);
            }
        }
    }, [activeStep, workflowType, verificationStatus]);


    const getBannerContent = () => {
        if (verificationStatus === VerificationStatus.NO_FACE_DETECTED) {
            return { message: "No face detected in the image. Please retake the photo.", isError: true };
        }
        if (verificationStatus === VerificationStatus.SERVICE_UNAVAILABLE) {
            return { message: "Verification service is currently unavailable. Please try again.", isError: true };
        }
        if ((verificationStatus === VerificationStatus.START || verificationStatus === VerificationStatus.IMAGE_UPLOADED) && activeStep === 1) {
            return { message: "Processing photo...", isError: false };
        }

        if (workflowType === WorkflowType.PATIENT_REFERENCE) {
            if (activeStep === 0) return { message: "Patient's photo is not registered. Take photo of patient's face to register their face on their profile." };
            if (activeStep === 2) return { message: 'Success! Click "Proceed" to store photo in patients record' };
        }
        else if ([WorkflowType.BOOKING_VERIFICATION, WorkflowType.PROCEDURE_VERIFICATION].includes(workflowType) ) {
            if (activeStep === 0) {
                return { message: "Patient's identity needs to be verified. Take photo of patient's face for facial recognition." };
            }
            if (activeStep === 2) {
                if (verificationStatus === VerificationStatus.FACE_MATCH) return { message: 'Patient identity verified. Click "Proceed" to continue' };
                if (verificationStatus === VerificationStatus.NO_FACE_MATCH) {
                    if (showDeclaration) {
                        if (declarationType === 'same-person') {
                            return {
                                message:
                                    'Please confirm that the patient matches the patient on record',
                                isError: true
                            };
                        }
                        if (declarationType === 'different-person') {
                            return {
                                message:
                                    'Facial verification failed.',
                                isError: true
                            };
                        }
                        return {
                            message: "Patient face does not match photo on patient's file. Please confirm patient identity",
                            isError: true
                        };
                    }
                    return {
                        message: "Patient face does not match photo on patient's file. Please confirm patient identity",
                        isError: true
                    };
                }
            }
        }

        return { message: 'Processing...', isError: false };
    };

    const handlePhotoCapture = () => {
        setCapturedImageUrl(imageUrl);
        setActiveStep(1);
    };


    const handleRetakePhoto = async () => {
        setActiveStep(0);
        setCapturedImageUrl(null);

        try {
            restartVerification();
        } catch (error) {
            console.error('Failed to create new verification task:', error);
        }
    };

    const handleSamePerson = () => {
        setDeclarationType('same-person');
        setShowDeclaration(true);
    };

    const handleDifferentPerson = () => {
        setDeclarationType('different-person');
        setShowDeclaration(true);
    };

    const handleGoBack = () => {
        setShowDeclaration(false);
        setDeclarationConfirmed(false);
    };

    // TODO: ADD METADATA TO BOOKING OR CONSULT
    // TODO: Remove hanging booking if it is unverified
    const handleProceed = async() => {
        if (declarationType === 'same-person') {
            try {
                await onProceed(workflowType);
                await overrideFacialRecognitionTask(workflowType, task_id);
            } catch (error) {
                console.error('Failed to override facial recognition:', error);
                enqueueSnackbar('Failed to override facial recognition. Please try again.', { variant: 'error' });
            }
        } else if (declarationType === 'different-person') {
            try {
                await markFacialRecognitionTaskUnverified(workflowType, task_id);
                navigate('/dashboard');
            } catch (error) {
                console.error('Failed to mark task as unverified:', error);
                enqueueSnackbar('Failed to mark task as unverified. Please try again.', { variant: 'error' });
            }
        }
        else{
            await onProceed(workflowType);
            await markFacialRecognitionTaskVerified(workflowType, task_id)
        }
    }
    const handleManualSubmit = async () => {
        try {
            await onProceed(workflowType, true);
            await overrideFacialRecognitionTask(workflowType, task_id);
        } catch (error) {
            console.error('Failed to override facial recognition:', error);
            enqueueSnackbar('Failed to override facial recognition. Please try again.', { variant: 'error' });
        }
    };

    const handleRetryVerification = async () => {
        try {
            await retryFacialRecognitionVerification(task_id);
            console.log('Verification retry initiated');
        } catch (error) {
            console.error('Failed to retry verification:', error);
        }
    };

    const steps = ["Take a photo of patient's face", "Verify photo", "Confirm patient identity"];
    const bannerContent = getBannerContent();
    const showBanner = bannerContent?.message;

    const patientInfoParts = [
        `${patient.first_name} ${patient.last_name}`,
        patient.alternative_identifiers[0].value,
    ];
    if (demographics?.job_title) {
        patientInfoParts.push(demographics.job_title.value);
    }
    if (demographics?.employee_number) {
        patientInfoParts.push(`Employee #: ${demographics.employee_number.value}`);
    }
    const patientInfoString = patientInfoParts.join(' | ');

    // TODO DISPLAY PATIENT INFORMATION AS PER THE UI DESIGN
    return (
        <ContainerPaper style={{ boxShadow: 'none', border: 'none' }}>
            <div className={classes.title}>Facial ID Verification</div>
            <PatientInfoContainer>
                <p>
                    {patientInfoString}
                </p>
            </PatientInfoContainer>
            <MaterialDivider variant="fullWidth" />

            {showBanner && (
                <GreyPaperContainer
                    square={false}
                    style={{
                        backgroundColor: bannerContent.isError ? '#ffebee' : undefined,
                        borderLeft: bannerContent.isError ? '4px solid #f44336' : undefined,
                        marginLeft: '0px',
                        marginRight: '0px'
                    }}
                >
                    <BannerMessageContainer>
                        <p style={{ color: bannerContent.isError ? '#d32f2f' : undefined }}>
                            {bannerContent.message}
                        </p>
                    </BannerMessageContainer>
                </GreyPaperContainer>
            )}

            <Stepper
                style={{ padding: 24 }}
                alternativeLabel
                activeStep={activeStep}
                connector={<ColorlibConnector />}
            >
                {steps.map((label, index) => (
                    <Step key={label}>
                        <StepLabel StepIconComponent={ColorlibStepIcon}>
                            Step {index + 1} {label}
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            {activeStep === 0 && (
                <>
                    <div className={classes.uppyContainer}>
                        <PhotoUploader
                            getFacialRecognitionPresignedUrl={getFacialRecognitionPresignedUrl}
                            onPhotoCapture={handlePhotoCapture}
                        />
                    </div>
                    <MaterialDivider variant="fullWidth" />
                    <ManualVerificationForm
                        confirmed={confirmed}
                        setConfirmed={setConfirmed}
                        onSubmit={handleManualSubmit}
                    />
                </>
            )}
            {activeStep === 1 && (
                <>
                    <div className={classes.imageVerificationContainer}>
                        <ImageDisplay
                            imageUrl={capturedImageUrl}
                            alt="Captured patient photo"
                            className={classes.imageContainer}
                        >
                            {(verificationStatus === VerificationStatus.START || verificationStatus === VerificationStatus.IMAGE_UPLOADED) && (
                                <div className={classes.verifyingOverlay}>
                                    <CircularProgress size={60} />
                                    <p>Verifying...</p>
                                </div>
                            )}
                            {(verificationStatus === VerificationStatus.NO_FACE_DETECTED || verificationStatus === VerificationStatus.SERVICE_UNAVAILABLE) && (
                                <div className={classes.errorOverlay}>
                                    <div className={classes.errorIcon}>
                                        <WarningAmberOutlinedIcon style={{ fontSize: 250 }}/>
                                    </div>
                                </div>
                            )}
                        </ImageDisplay>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0', minHeight: '60px', alignItems: 'center' }}>
                        {(verificationStatus === VerificationStatus.NO_FACE_DETECTED || verificationStatus === VerificationStatus.SERVICE_UNAVAILABLE) && (
                            <Button
                                variant="contained"
                                onClick={verificationStatus === VerificationStatus.NO_FACE_DETECTED ? handleRetakePhoto : handleRetryVerification}
                                color="primary"
                                style={{ minWidth: '150px' }}
                            >
                                {verificationStatus === VerificationStatus.NO_FACE_DETECTED ? 'Retake Photo' : 'Retry Verification'}
                            </Button>
                        )}
                    </div>

                    {verificationStatus === VerificationStatus.SERVICE_UNAVAILABLE && (
                        <>
                            <MaterialDivider variant="fullWidth" />
                            <ManualVerificationForm
                                confirmed={confirmed}
                                setConfirmed={setConfirmed}
                                onSubmit={handleManualSubmit}
                            />
                        </>
                    )}
                </>
            )}
            {activeStep === 2 && (
                <div className={classes.step3Container}>
                    {(
                        (workflowType === WorkflowType.PATIENT_REFERENCE && (verificationStatus === VerificationStatus.FACE_DETECTED || verificationStatus === VerificationStatus.MANUAL_VERIFICATION || verificationStatus === VerificationStatus.MANUAL_OVERRIDE)) ||
                        ([WorkflowType.BOOKING_VERIFICATION, WorkflowType.PROCEDURE_VERIFICATION].includes(workflowType) && (verificationStatus === VerificationStatus.FACE_MATCH))
                    ) && (
                        <div>
                            <ImageDisplay
                                imageUrl={capturedImageUrl}
                                alt="Verified patient photo"
                                className={classes.imageContainer}
                            >
                                <div className={classes.successOverlay}>
                                    <div className={classes.checkIcon}>
                                        <CheckCircleOutlinedIcon style={{ fontSize: 250 }}/>
                                    </div>
                                </div>
                            </ImageDisplay>

                            {workflowType === WorkflowType.PATIENT_REFERENCE && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    marginTop: '16px'
                                }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                            type="checkbox"
                                            checked={patientIdVerified}
                                            onChange={(e) => setPatientIdVerified(e.target.checked)}
                                        />
                                        I confirm that I have personally verified the patient's identity using valid ID
                                    </label>
                                </div>
                            )}

                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                margin: '20px 0', 
                                minHeight: '60px', 
                                alignItems: 'center',
                                gap: '12px' 
                            }}>

                            {workflowType === WorkflowType.PATIENT_REFERENCE && (
                                <Button
                                    variant="outlined"
                                    onClick={handleRetakePhoto}
                                    style={{ minWidth: '150px' }}
                                >
                                    Retake Photo
                                </Button>
                            )}

                                <Button
                                    variant="contained"
                                    onClick={handleProceed}
                                    color="primary"
                                    style={{ minWidth: '150px' }}
                                    disabled={workflowType === WorkflowType.PATIENT_REFERENCE && !patientIdVerified}
                                >
                                    Proceed
                                </Button>
                            </div>
                        </div>
                    )}

                    {verificationStatus === VerificationStatus.NO_FACE_MATCH && [WorkflowType.BOOKING_VERIFICATION, WorkflowType.PROCEDURE_VERIFICATION].includes(workflowType) && !showDeclaration && (
                        <>
                            <div className={classes.imageComparisonContainer}>
                                <div className={classes.imageWithTitle}>
                                    <h4>Recent uploaded photo</h4>
                                    <ImageDisplay
                                        imageUrl={capturedImageUrl}
                                        alt="Recently captured photo"
                                        className={classes.imageContainer}
                                    />
                                </div>
                                <div className={classes.imageWithTitle}>
                                    <h4>Photo on patients file</h4>
                                    <ImageDisplay
                                        imageUrl={originalImageUrl}
                                        alt="Original patient photo"
                                        className={classes.imageContainer}
                                    />
                                </div>
                            </div>
                            <div className={classes.rightAlignedButtons}>
                                <Button variant="outlined" onClick={handleRetakePhoto} style={{ minWidth: '150px' }}>
                                    Retake Photo
                                </Button>
                                <Button variant="outlined" onClick={handleSamePerson} style={{ minWidth: '150px' }}>
                                    Same Person
                                </Button>
                                <Button variant="contained" onClick={handleDifferentPerson} color="primary" style={{ minWidth: '150px' }}>
                                    Different Person
                                </Button>
                            </div>
                        </>
                    )}

                    {showDeclaration && [WorkflowType.BOOKING_VERIFICATION, WorkflowType.PROCEDURE_VERIFICATION].includes(workflowType) && (
                        <>
                            <div style={{ display: 'flex', gap: '24px' }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    paddingTop: '43px',
                                    width: '120px',
                                    flexShrink: 0
                                }}>
                                    <WarningAmberOutlinedIcon style={{ color: 'red', fontSize: '120px' }} />
                                </div>
                                <VerifyManualContainer style={{ flex: 1 }}>
                                    <h3>
                                        {declarationType === 'same-person'
                                            ? 'You have chosen to manually confirm the patients identity despite the facial recognition system indicating a low match. Please review and confirm the statements below to proceed:'
                                            : 'You have indicated that the patient does not match the individual on record. If you continue, this consultation will be flagged as a potential fraud attempt.'
                                        }
                                    </h3>
                                    <ol>
                                        <li>
                                            I confirm that the patient {declarationType === 'same-person' ? 'matches' : 'does not match'} the individual whose photo is on record
                                        </li>
                                        <li>
                                            I understand that this {declarationType === 'same-person' ? 'manual confirmation' : 'action'} will be flagged and logged for audit purposes.
                                        </li>
                                    </ol>
                                    <label style={{ marginTop: '16px', display: 'flex', gap: '6px' }}>
                                        <input
                                            type="checkbox"
                                            checked={declarationConfirmed}
                                            onChange={(e) => setDeclarationConfirmed(e.target.checked)}
                                        />
                                        Confirm
                                    </label>
                                </VerifyManualContainer>
                            </div>
                            <div className={classes.rightAlignedButtons}>
                                <Button variant="outlined" onClick={handleGoBack} style={{ minWidth: '150px' }}>
                                    Go Back
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleProceed}
                                    color="primary"
                                    disabled={!declarationConfirmed}
                                    style={{ minWidth: '150px' }}
                                >
                                    Proceed
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </ContainerPaper>
    );
};


export default FacialIDVerification;
